import { PageHero, SectionHeading } from "@/components/Section";
import { IMAGES } from "@/data/site";
import { Link } from "react-router-dom";
import { Wrench, Building2, Mountain, Truck, Package, Hammer, ArrowRight } from "lucide-react";

// Full-width banner placed at the top of a category section
const CategoryBanner = ({ image, label, title, overlay = 0.55, gold = false }) => (
  <div className="relative w-full overflow-hidden" data-testid={`banner-${label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
    <img src={image} alt={title} loading="lazy" className="w-full h-[360px] md:h-[420px] object-cover object-center" />
    <div className="absolute inset-0" style={{ background: `rgba(10,22,40,${overlay})` }} />
    <div className="absolute inset-0 flex items-end">
      <div className="max-w-[1400px] mx-auto w-full px-5 lg:px-10 pb-10 md:pb-14">
        <div className={`text-[12px] font-bold tracking-[.22em] mb-3 ${gold ? "text-[#C8973C]" : "text-[#E8721C]"}`}>{label}</div>
        <h2 className={`font-display text-[40px] md:text-[60px] heading-bar ${gold ? "heading-bar-gold" : ""} text-white leading-[1]`}>{title}</h2>
      </div>
    </div>
  </div>
);

// Default product list block (image + bullets)
const CategoryBlock = ({ id, Icon, title, desc, items, image, imageRight }) => (
  <section id={id} className="py-20 border-t border-[#0A1628]/10" data-testid={`category-${id}`}>
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
      <div className="lg:col-span-5">
        <div className="relative">
          <div className="absolute -top-3 -left-3 right-3 bottom-3 border-2 border-[#E8721C]"/>
          <img src={image} alt={title} loading="lazy" className="relative w-full h-[340px] object-cover"/>
        </div>
      </div>
      <div className="lg:col-span-7">
        <div className="flex items-center gap-3 text-[#E8721C] font-bold tracking-[.2em] text-[12px] mb-3">
          <Icon size={20}/> CATEGORY
        </div>
        <h2 className="font-display text-[38px] md:text-[48px] heading-bar leading-[1]">{title}</h2>
        <p className="mt-5 text-[#1E2D40] text-[16px] leading-7 max-w-2xl">{desc}</p>
        <div className="grid sm:grid-cols-2 gap-8 mt-7 items-start">
          <ul className="grid gap-y-2 text-[#1E2D40]">
            {items.map((it) => (
              <li key={it} className="flex gap-2"><span className="text-[#E8721C] font-bold">›</span>{it}</li>
            ))}
          </ul>
          {imageRight && (
            <div className="relative">
              <div className="absolute -top-2 -right-2 left-2 bottom-2 border-2 border-[#E8721C]"/>
              <img src={imageRight} alt={`${title} – detail`} loading="lazy" className="relative w-full h-[260px] object-cover"/>
            </div>
          )}
        </div>
      </div>
    </div>
  </section>
);

// Building Materials — six sub-section cards (each with its own image)
const BuildingSubsections = () => {
  const subs = [
    { t: "FOUNDATION & STRUCTURE", img: IMAGES.subFoundation,
      list: ["Cement (various grades) & aggregates","Reinforcing steel & mesh","Damp proof membranes","Foundation drainage"] },
    { t: "BRICKS & BLOCKS", img: IMAGES.subBricks,
      list: ["Concrete blocks","Clay bricks","Mortars & screeds","Sand, stone & aggregates"] },
    { t: "ROOFING", img: IMAGES.subRoofing,
      list: ["Corrugated iron sheeting","IBR roof sheeting","Roof tiles & insulation","Flashing, fascia & gutters"] },
    { t: "TILES, FLOORS & WALLS", img: IMAGES.subTiles,
      list: ["Floor tiles (ceramic, porcelain)","Wall tiles & adhesives","Screeds & levelling","Waterproofing for wet areas"] },
    { t: "DOORS, WINDOWS & FRAMES", img: IMAGES.subDoors,
      list: ["Steel security doors","Wooden doors","Aluminium & steel window frames","Glass panes & door liners"] },
    { t: "PAINT, PLASTER & FINISHES", img: IMAGES.subPaint,
      list: ["Interior & exterior paint","Primers & undercoats","Plaster, putty & fillers","Cornices & skirtings"] },
  ];
  return (
    <section className="bg-[#F8F6F2] py-20" data-testid="building-subsections">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <SectionHeading label="FOUNDATION TO ROOF" title="EVERY STAGE OF THE BUILD." subtitle="The complete building lifecycle in one supply partner. Whatever stage of the build, we have it." />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subs.map((s) => (
            <div key={s.t} className="cg-card p-0 overflow-hidden h-full" data-testid={`sub-${s.t.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
              <div className="relative h-44 overflow-hidden">
                <img src={s.img} alt={s.t} loading="lazy" className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/85 via-[#0A1628]/20 to-transparent"/>
                <div className="absolute bottom-3 left-4 right-4 font-display text-white text-[18px] tracking-wide">{s.t}</div>
              </div>
              <div className="p-6">
                <ul className="space-y-1.5 text-[#1E2D40] text-[15px]">
                  {s.list.map((it) => <li key={it} className="flex gap-2"><span className="text-[#E8721C] font-bold">›</span>{it}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Module-level constants — referentially stable across renders
const HARDWARE_ITEMS = [
  "Hand tools (hammers, screwdrivers, pliers, levels)",
  "Power tools (drills, grinders, circular saws)",
  "Fasteners & fixings (bolts, nuts, screws, anchors)",
  "Adhesives & sealants",
  "Safety & PPE",
  "Site consumables",
  "Locks, hinges & architectural hardware",
  "Workshop & storage equipment",
];
const MINING_ITEMS = [
  "Drilling equipment & consumables",
  "Ventilation systems & fans",
  "Conveyor components & belting",
  "Rock bolts & ground support",
  "Mining safety equipment & PPE",
  "Blasting accessories (non-explosive)",
  "Underground lighting",
  "Mine dewatering pumps",
];
const HEAVY_ITEMS = [
  "Earthmoving (excavators, loaders, bulldozers)",
  "Material handling (forklifts, cranes)",
  "Generators (diesel & petrol, various kVA)",
  "Air compressors",
  "Water pumps & booster pumps",
  "Industrial motors & drives",
  "Hydraulic equipment",
  "Welding & scaffolding systems",
];
const COMMODITIES_ITEMS = [
  "Steel products (sheet, pipe, bar, tube)",
  "Copper & copper products",
  "Construction chemicals & additives",
  "Raw materials for manufacturing",
  "Agricultural commodities (on enquiry)",
  "Specialist commodities (sourced on request)",
];
const RENOVATION_ITEMS = [
  "Plaster, paint & finishes",
  "Floor & wall tiles",
  "Adhesives & grouts",
  "Doors, frames & ironmongery",
  "Sanitary ware & plumbing",
  "Electrical fittings & cable",
  "Skirting, cornices & trims",
  "Site safety & PPE",
];
const JUMP_LINKS = [
  ["hardware","Hardware"],["building","Building Materials"],["mining","Mining"],
  ["heavy","Heavy Equipment"],["commodities","Commodities"],["renovation","Renovation"],
];

const JumpBar = () => (
  <div className="bg-[#E8721C] text-[#0A1628]" data-testid="jump-bar">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-5 flex flex-wrap items-center gap-6">
      <span className="font-bold tracking-[.2em] text-[12px]">JUMP TO:</span>
      {JUMP_LINKS.map(([id, label]) => (
        <a key={id} href={`#${id}`} className="text-[13px] font-bold tracking-[.06em] uppercase hover:underline">{label}</a>
      ))}
    </div>
  </div>
);

const BuildingSection = () => (
  <section id="building" data-testid="category-building">
    <CategoryBanner
      image={IMAGES.buildingMaterialsBanner}
      label="BUILDING MATERIALS"
      title="FOUNDATION TO ROOF — EVERYTHING IN ONE SUPPLY PARTNER."
      overlay={0.5}
    />
    <BuildingSubsections />
  </section>
);

const MiningSection = () => (
  <section id="mining" data-testid="category-mining">
    <CategoryBanner
      image={IMAGES.miningBanner}
      label="MINING EQUIPMENT"
      title="INDUSTRIAL-GRADE MINING SUPPLY."
      overlay={0.55}
    />
    <CategoryBlock id="mining-detail" Icon={Mountain} title="MINING EQUIPMENT & SUPPLIES" image={IMAGES.miningAlt}
      imageRight={IMAGES.miningPPE}
      desc="Supplying mining operations across regions with reliable, industrial-grade equipment and supplies — from drilling to safety PPE."
      items={MINING_ITEMS}
    />
  </section>
);

const CommoditiesSection = () => (
  <section id="commodities" data-testid="category-commodities">
    <CategoryBanner
      image={IMAGES.commoditiesBanner}
      label="COMMODITIES & BULK"
      title="STEEL. COPPER. RAW MATERIALS — AT SCALE."
      overlay={0.5}
    />
    <CategoryBlock id="commodities-detail" Icon={Package} title="COMMODITIES & BULK SUPPLY" image={IMAGES.steel}
      imageRight={IMAGES.commoditiesForklift}
      desc="Bulk commodity supply for large projects and export clients. Consistent quality, competitive pricing, flexible volumes."
      items={COMMODITIES_ITEMS}
    />
  </section>
);

const SourcingCta = () => (
  <section className="bg-[#0A1628] py-20 text-white blueprint-grid">
    <div className="max-w-[1100px] mx-auto px-5 lg:px-10 text-center">
      <h3 className="font-display text-[36px] md:text-[52px] leading-[1]">CAN'T FIND WHAT YOU NEED? <span className="text-[#E8721C]">WE SOURCE IT.</span></h3>
      <p className="mt-5 text-[#8A96A3] text-[17px] max-w-2xl mx-auto">Our supplier network is extensive. If it's related to hardware, construction, mining or industrial supply — we'll find it for you.</p>
      <div className="mt-8"><Link to="/contact" className="btn-orange">Send a Sourcing Enquiry <ArrowRight size={14}/></Link></div>
    </div>
  </section>
);

const WhatWeSupply = () => (
  <>
    <PageHero
      label="WHAT WE SUPPLY"
      title="500+ PRODUCT CATEGORIES. EVERYTHING YOUR PROJECT NEEDS."
      subtitle="Hardware. Building materials. Mining equipment. Heavy machinery. Commodities. From the first foundation block to the final roof tile — and everything in between."
      image={IMAGES.warehouse}
    />
    <JumpBar />
    <CategoryBlock id="hardware" Icon={Wrench} title="HARDWARE & TOOLS" image={IMAGES.hardware}
      desc="A comprehensive range of hardware for construction sites, maintenance teams and hardware retailers."
      items={HARDWARE_ITEMS}
    />
    <BuildingSection />
    <MiningSection />
    <CategoryBlock id="heavy" Icon={Truck} title="HEAVY & INDUSTRIAL EQUIPMENT" image={IMAGES.heavy}
      desc="Large-scale equipment supply for construction, mining and industrial operations — available for domestic supply and export."
      items={HEAVY_ITEMS}
    />
    <CommoditiesSection />
    <CategoryBlock id="renovation" Icon={Hammer} title="RENOVATION & CONSTRUCTION SUPPLIES" image={IMAGES.renoFoundation}
      desc="From the supply side, every consumable, finish and structural element required for renovation and construction projects."
      items={RENOVATION_ITEMS}
    />
    <SourcingCta />
  </>
);
export default WhatWeSupply;
